import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Printer, ArrowLeft, IndianRupee } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtINR = (paise) => `₹${(Number(paise || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' }) : '—';

const STATUS_LABEL = {
  captured: 'Paid',
  refunded: 'Refunded',
  failed: 'Failed',
  created: 'Pending',
  signature_failed: 'Failed (signature mismatch)',
};

const InvoicePage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('tgmart_vendor_token');
    if (!token) { navigate('/vendor/dashboard'); return; }
    axios.get(`${API}/payments/vendor/invoices/${orderId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [orderId, navigate]);

  if (loading) return <div className="min-h-screen bg-gray-100 flex items-center justify-center text-sm text-gray-500">Loading invoice…</div>;
  if (err || !data) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-gray-700">{err || 'Invoice not found.'}</p>
        <Button onClick={() => navigate('/vendor/dashboard')} variant="outline">Back to dashboard</Button>
      </div>
    );
  }

  const { order, vendor, company } = data;
  const invoiceNo = order.id ? `INV-${order.id.slice(0, 8).toUpperCase()}` : '—';
  const subtotal = Number(order.amount || 0);
  const gst = 0;  // GST handled at Razorpay; invoice shows gross only
  const total = subtotal + gst;
  const meta = order.meta || {};
  const lineDesc = order.type === 'plan'
    ? `${meta.plan_name || 'Plan'} subscription`
    : order.type === 'credits'
      ? `Lead credits top-up — ${meta.credits || 0} leads @ ₹${meta.credits_inr || '?'}/lead`
      : order.description || 'Payment';

  return (
    <div className="min-h-screen bg-gray-100 py-6">
      {/* Toolbar (hidden when printing) */}
      <div className="max-w-3xl mx-auto px-4 mb-4 flex items-center justify-between flex-wrap gap-2 print:hidden">
        <Button variant="outline" onClick={() => navigate('/vendor/dashboard')} data-testid="invoice-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={() => window.print()} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="invoice-print-btn">
          <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
        </Button>
      </div>

      {/* Invoice sheet */}
      <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg p-8 print:shadow-none print:rounded-none print:p-6" data-testid="invoice-sheet">
        <div className="flex items-start justify-between flex-wrap gap-4 border-b border-gray-200 pb-5">
          <div>
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name} className="h-12 mb-2" />
            ) : (
              <div className="text-2xl font-extrabold">
                <span className="text-[#ee5e23]">telangana</span>Mart
              </div>
            )}
            <div className="text-xs text-gray-600 mt-2 leading-relaxed">
              {company.address && <div>{company.address}</div>}
              {company.phone && <div>{company.phone}</div>}
              {company.email && <div>{company.email}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-gray-500">Tax Invoice</div>
            <div className="text-xl font-bold mt-1" data-testid="invoice-number">{invoiceNo}</div>
            <div className="text-xs text-gray-500 mt-1">{fmtDate(order.created_at)}</div>
            <span className={`mt-2 inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded ${
              order.status === 'captured' ? 'bg-emerald-100 text-emerald-800' :
              order.status === 'refunded' ? 'bg-amber-100 text-amber-800' :
              order.status === 'failed' || order.status === 'signature_failed' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-700'}`} data-testid="invoice-status">
              {STATUS_LABEL[order.status] || order.status}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-5 border-b border-gray-200">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Billed to</div>
            <div className="text-sm font-semibold text-gray-900 mt-1">{vendor.name}</div>
            {vendor.vendor_code && (
              <div className="text-[11px] font-mono uppercase tracking-wider text-gray-500 mt-0.5" data-testid="invoice-vendor-code">
                Vendor ID: {vendor.vendor_code}
              </div>
            )}
            <div className="text-xs text-gray-600 mt-0.5">
              {vendor.address && <div>{vendor.address}</div>}
              {vendor.city && <div>{vendor.city}</div>}
              {vendor.mobile && <div>+91 {vendor.mobile}</div>}
              {vendor.email && <div>{vendor.email}</div>}
              {vendor.gst && <div className="mt-1">GST: {vendor.gst}</div>}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Payment</div>
            <div className="text-xs text-gray-600 mt-1 space-y-0.5">
              <div>Mode: <strong>Razorpay</strong> ({(order.mode || 'test').toUpperCase()})</div>
              {order.razorpay_order_id && <div>Order: <span className="font-mono">{order.razorpay_order_id}</span></div>}
              {order.razorpay_payment_id && <div>Payment: <span className="font-mono">{order.razorpay_payment_id}</span></div>}
              {order.captured_at && <div>Captured: {fmtDate(order.captured_at)}</div>}
              {order.refund_id && <div>Refund: <span className="font-mono">{order.refund_id}</span></div>}
            </div>
          </div>
        </div>

        <table className="w-full text-sm my-4">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
              <th className="py-2 pr-2">Description</th>
              <th className="py-2 px-2 text-right">Qty</th>
              <th className="py-2 pl-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-3 pr-2">{lineDesc}</td>
              <td className="py-3 px-2 text-right">1</td>
              <td className="py-3 pl-2 text-right font-mono">{fmtINR(subtotal)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="text-sm">
              <td className="py-1.5"></td>
              <td className="py-1.5 px-2 text-right text-gray-500">Subtotal</td>
              <td className="py-1.5 pl-2 text-right font-mono">{fmtINR(subtotal)}</td>
            </tr>
            <tr className="text-sm">
              <td className="py-1.5"></td>
              <td className="py-1.5 px-2 text-right text-gray-500">GST / Tax</td>
              <td className="py-1.5 pl-2 text-right font-mono">{fmtINR(gst)}</td>
            </tr>
            <tr className="border-t border-gray-300 text-base font-bold">
              <td className="py-2"></td>
              <td className="py-2 px-2 text-right">Total</td>
              <td className="py-2 pl-2 text-right font-mono text-[#ee5e23] inline-flex items-center justify-end gap-0.5" data-testid="invoice-total">
                <IndianRupee className="w-4 h-4" />{(total / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>
          </tfoot>
        </table>

        <div className="text-xs text-gray-500 leading-relaxed pt-4 border-t border-gray-200">
          <p>Thank you for choosing {company.name || 'TelanganaMart'}. This is a system-generated invoice and does not require a signature.</p>
          <p className="mt-1">For any payment-related queries, please contact us at <strong>{company.email || '—'}</strong>.</p>
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;
